import { Fragment } from 'react';
import { Form, Nav, OverlayTrigger, Tooltip } from 'react-bootstrap';
import HelpIcon from './HelpIcon';
import { useSidebar } from '../contexts/SidebarContext';

/**
 * The section list of an admin page (System Admin, Org Admin), inside its Tab.Container. On a wide screen it is a
 * fixed column of links under group headings; on a phone, where a column would push the page down, it is one
 * "Section" picker above the content instead.
 *
 * When the app's sidebar is collapsed the column narrows to icons too (names show on hover, group headings
 * become thin dividers), so the one sidebar button makes room for the page.
 *
 * sections: [{ group?: string, items: [{ key, label, icon, help?: boolean }] }]. An item with help shows a help
 * icon that calls onHelp(key).
 */
function AdminSectionNav({ sections, activeKey, onSelect, onHelp, ariaLabel = 'Sections' }) {
  const { collapsed } = useSidebar();

  return (
    <>
      <div className={`admin-sidenav-col d-none d-md-block${collapsed ? ' admin-sidenav-collapsed' : ''}`}>
        <Nav variant="pills" className="flex-column admin-sidenav" aria-label={ariaLabel}>
          {sections.map((section, index) => (
            <Fragment key={section.group || `section-${index}`}>
              {section.group && (collapsed
                ? <hr className="admin-nav-divider" aria-hidden="true" />
                : <div className="admin-nav-group" role="presentation">{section.group}</div>)}
              {section.items.map((item) => (
                <Nav.Item key={item.key}>
                  {collapsed ? (
                    <OverlayTrigger placement="right" overlay={<Tooltip id={`section-tip-${item.key}`}>{item.label}</Tooltip>}>
                      <Nav.Link eventKey={item.key} aria-label={item.label}>
                        <i className={`bi ${item.icon}`} aria-hidden="true" />
                      </Nav.Link>
                    </OverlayTrigger>
                  ) : (
                    <Nav.Link eventKey={item.key}>
                      <i className={`bi ${item.icon} me-2`} aria-hidden="true" />
                      {item.help && onHelp ? (
                        <span className="admin-navlabel admin-navlabel-help">
                          {item.label}
                          <HelpIcon onClick={(e) => { e.stopPropagation(); onHelp(item.key); }} />
                        </span>
                      ) : (
                        <span className="admin-navlabel">{item.label}</span>
                      )}
                    </Nav.Link>
                  )}
                </Nav.Item>
              ))}
            </Fragment>
          ))}
        </Nav>
      </div>

      <div className="d-md-none mb-3 d-flex align-items-center gap-2 admin-section-picker">
        <Form.Label htmlFor="admin-section-picker" className="mb-0 small text-muted flex-shrink-0">Section</Form.Label>
        <Form.Select id="admin-section-picker" value={activeKey} onChange={(e) => onSelect(e.target.value)}>
          {sections.map((section, index) => {
            const options = section.items.map((item) => <option key={item.key} value={item.key}>{item.label}</option>);
            return section.group
              ? <optgroup key={section.group} label={section.group}>{options}</optgroup>
              : <Fragment key={`section-${index}`}>{options}</Fragment>;
          })}
        </Form.Select>
        {onHelp && sections.some((s) => s.items.some((i) => i.key === activeKey && i.help)) && (
          <HelpIcon onClick={() => onHelp(activeKey)} />
        )}
      </div>
    </>
  );
}

export default AdminSectionNav;
